/**
 * Authentication Hook and Context
 * 
 * Provides authentication state and methods throughout the app.
 * Handles JWT token storage, user info, and login/logout flows.
 * 
 * Usage:
 *   const { user, isAuthenticated, login, logout } = useAuth()
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react'
import { setToken, clearToken } from '@/api/client'
import api from '@/api/client'

/**
 * User information from the JWT token / API.
 */
export interface User {
  id: string
  twitchId: string
  username: string
  isAdmin: boolean
}

/**
 * Auth context value.
 */
interface AuthContextValue {
  /** Current user, or null if not authenticated */
  user: User | null
  /** Whether the user is authenticated */
  isAuthenticated: boolean
  /** Whether we're currently checking auth status */
  isLoading: boolean
  /** Start the Twitch OAuth flow */
  login: () => void
  /** Log out and clear tokens */
  logout: () => void
  /** Update user data (after profile changes) */
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * Decode a JWT token to extract the payload.
 * Note: This does NOT verify the signature (that's the backend's job).
 */
function decodeJwt(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    
    const payload = parts[1]
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(decoded)
  } catch {
    return null
  }
}

/**
 * Check if a JWT token is expired.
 */
function isTokenExpired(token: string): boolean {
  const payload = decodeJwt(token)
  if (!payload || typeof payload.exp !== 'number') return true
  
  // Check if expired (with 60s buffer)
  return payload.exp * 1000 < Date.now() - 60000
}

/**
 * Extract user info from a JWT token.
 */
function userFromToken(token: string): User | null {
  const payload = decodeJwt(token)
  if (!payload) return null
  
  return {
    id: String(payload.sub || ''),
    twitchId: String(payload.sub || ''),
    username: String(payload.username || 'Unknown'),
    // The backend mints this as `is_super_admin` (routes/auth.py passes it via
    // extra_claims; core/auth.py adds nothing else). Reading `is_admin` here
    // silently produced false for EVERY user, which hid the whole Admin nav
    // block and bounced /admin/* back to the dashboard.
    isAdmin: Boolean(payload.is_super_admin),
  }
}

/**
 * Shape of `GET /auth/me` (`pratbot/api/routes/auth.py::UserResponse`).
 *
 * Declared explicitly because the API speaks snake_case and this module speaks
 * camelCase. Casting the body straight to `User` — which is what this file did
 * previously — type-checks cleanly and silently yields `isAdmin: undefined`,
 * dropping the caller out of the admin UI with no error anywhere.
 */
interface MeResponse {
  id: number
  twitch_id: string
  twitch_username: string
  twitch_email: string | null
  is_super_admin: boolean
  is_active: boolean
}

/**
 * Map the API body onto `User`.
 *
 * `id` is deliberately the TWITCH id, not the response's `id`: the token path
 * fills `id` from the JWT `sub` claim, which is a Twitch id. If the two paths
 * disagreed, `user.id` would mean different things depending on whether the
 * user came from the stored token or from a refresh. The internal `users.id`
 * is not carried — nothing in the UI needs it.
 */
function userFromMe(data: MeResponse): User {
  return {
    id: String(data.twitch_id),
    twitchId: String(data.twitch_id),
    username: data.twitch_username || 'Unknown',
    isAdmin: Boolean(data.is_super_admin),
  }
}

/**
 * AuthProvider - Wraps the app to provide auth context.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  
  /**
   * Check stored token and set user on mount.
   */
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('pratbot_token')
      
      if (token && !isTokenExpired(token)) {
        const userData = userFromToken(token)
        setUser(userData)
      } else if (token) {
        // Token expired, clear it
        clearToken()
      }
      
      setIsLoading(false)
    }
    
    checkAuth()
  }, [])
  
  /**
   * Start Twitch OAuth flow.
   */
  const login = useCallback(() => {
    // Redirect to our backend's OAuth start endpoint
    window.location.href = '/auth/twitch'
  }, [])
  
  /**
   * Log out and clear all auth state.
   */
  const logout = useCallback(() => {
    clearToken()
    setUser(null)
    // Optionally redirect to login
    window.location.href = '/login'
  }, [])
  
  /**
   * Refresh user data from the API.
   */
  const refreshUser = useCallback(async () => {
    try {
      const userData = await api.get<MeResponse>('/auth/me')
      setUser(userFromMe(userData))
    } catch (error) {
      console.error('Failed to refresh user:', error)
      // If we get a 401, log out
      if (error instanceof Error && error.message.includes('401')) {
        logout()
      }
    }
  }, [logout])
  
  const value: AuthContextValue = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshUser,
  }
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

/**
 * useAuth hook - Access auth context.
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  
  return context
}

/**
 * How the CURRENT session was obtained: `'break_glass'`, or null for an
 * ordinary Twitch OAuth session (which mints no `auth_method` claim).
 *
 * Read straight from the stored JWT rather than carried on `User`, and that is
 * deliberate. `/auth/me` does not return `auth_method`, so a field on `User`
 * would be populated by `userFromToken` and then silently blanked by the next
 * `refreshUser()` — which is precisely the bug that cost R1 its admin nav, in a
 * new costume. The token is the only artefact that actually carries the claim,
 * so the banner asks the token.
 */
export function getStoredAuthMethod(): string | null {
  const token = localStorage.getItem('pratbot_token')
  if (!token || isTokenExpired(token)) return null

  const payload = decodeJwt(token)
  if (!payload) return null

  return typeof payload.auth_method === 'string' ? payload.auth_method : null
}

/**
 * Handle the OAuth callback - extract token from URL fragment.
 * Call this from the /auth/complete page.
 */
export function handleAuthCallback(): boolean {
  // Token is in URL fragment: /auth/complete#token=xxx
  const hash = window.location.hash
  const match = hash.match(/token=([^&]+)/)
  
  if (match && match[1]) {
    const token = match[1]
    
    if (!isTokenExpired(token)) {
      setToken(token)
      return true
    }
  }
  
  return false
}
